"use client";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, Linkedin } from "lucide-react";
import Header from "../../Components/Header";
import HeroSection from "../../Components/HeroSection";
import ServicesSection from "../../Components/ServicesSection";
import AnalyticsSection from "../../Components/AnalyticsSection";
import ContactForm from "../../Components/ContactForm";
import Testimonials, { TestimonialsPro } from "../../Components/Testimonials";
import HowItWorks from "../../Components/HowItWorks";
import Footer from "../../Components/Footer";
import ReviewsSection from "../../Components/ReviewsSection";
import AboutSection from "../../Components/AboutSection";
import CursorEffect from "../../Components/CursorEffect";
import TeamSection from "../../Components/TeamSection";
import ChatBoatWidget from "../../Components/ChatBoatWidget";
import API_BASE_URL from "../../config/api";

export default function LandingPage() {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    linkedin: "",
  });
  const [errors, setErrors] = useState({});

  // ✅ Show modal only once per session
  useEffect(() => {
    const modalShown = sessionStorage.getItem("connectedModalShown");
    if (!modalShown) {
      const timer = setTimeout(() => {
        setShowModal(true);
        sessionStorage.setItem("connectedModalShown", "true");
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setShowModal(false);
    }
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  // ✅ Validation logic
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Phone validation (optional but must be digits if entered)
    if (formData.phone && !/^[0-9]{10,15}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number (10–15 digits).";
    }

    // LinkedIn validation (optional)
    if (
      formData.linkedin &&
      !/^https?:\/\/(www\.)?linkedin\.com\/.*$/i.test(formData.linkedin)
    ) {
      newErrors.linkedin = "Please enter a valid LinkedIn URL.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await fetch(`${API_BASE_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          phone: formData.phone,
          linkedin: formData.linkedin,
          source: 'landing_modal'
        })
      });
    } catch (err) {
      console.error('Failed to save submission');
    }

    const message = `
New Lead From Landing Page (Session Popup):
-------------------------------------------
📧 Email: ${formData.email}
📱 Phone: ${formData.phone || "Not provided"}
🔗 LinkedIn: ${formData.linkedin || "Not provided"}
  `;

    const encodedMessage = encodeURIComponent(message);
    const phone = "917057051648";
    window.location.href = `https://wa.me/${phone}?text=${encodedMessage}`;
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <Header />
      <ChatBoatWidget />
      <main>
        <CursorEffect />
        <HeroSection />
        <AboutSection />
        {/* <ReviewsSection /> */}
        {/* <TeamSection />  */}
        <ServicesSection />
        <HowItWorks />
        <AnalyticsSection />
        {/* <Testimonials /> */}
        {/* <TestimonialsPro /> */}
        <section className="py-20">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-4">Ready to get started?</h2>
            <p className="text-gray-600 mb-6">
              Request a free quick review and we will send a short audit with
              next steps.
            </p>
            <ContactForm />
          </div>
        </section>
      </main>
      <Footer />

      {/* --- CONNECT MODAL --- */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 10 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="relative bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 max-w-md w-full mx-4 p-8"
            >
              {/* Close Button */}
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition"
              >
                ✕
              </button>

              {/* Modal Content */}
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Let’s Stay <span className="text-indigo-600">Connected</span>
                </h2>
                <p className="text-gray-600">
                  Share your details below, and our team will personally reach
                  out to help you improve your digital presence.
                </p>
              </div>

              {/* FORM */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div className="relative">
                  <Mail
                    className="absolute left-3 top-3 text-indigo-500"
                    size={20}
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white/70 border ${
                      errors.email
                        ? "border-red-400 focus:ring-red-400"
                        : "border-indigo-100 focus:ring-indigo-400"
                    } focus:ring-2 transition-all placeholder-gray-400`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="relative">
                  <Phone
                    className="absolute left-3 top-3 text-indigo-500"
                    size={20}
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Your Contact / WhatsApp Number"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white/70 border ${
                      errors.phone
                        ? "border-red-400 focus:ring-red-400"
                        : "border-indigo-100 focus:ring-indigo-400"
                    } focus:ring-2 transition-all placeholder-gray-400`}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                {/* LinkedIn */}
                <div className="relative">
                  <Linkedin
                    className="absolute left-3 top-3 text-indigo-500"
                    size={20}
                  />
                  <input
                    type="url"
                    name="linkedin"
                    placeholder="LinkedIn Profile URL (optional)"
                    value={formData.linkedin}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white/70 border ${
                      errors.linkedin
                        ? "border-red-400 focus:ring-red-400"
                        : "border-indigo-100 focus:ring-indigo-400"
                    } focus:ring-2 transition-all placeholder-gray-400`}
                  />
                  {errors.linkedin && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.linkedin}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold shadow-lg hover:shadow-indigo-300/40 transition-all"
                  type="submit"
                >
                  Connect With Us
                </motion.button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-5">
                We respect your privacy — your info stays 100% secure.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
